import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import type { Json } from '@/integrations/supabase/types';
import { deleteCalendarEventsForScheduleItems } from '@/lib/deleteCalendarEvents';

// ── DB Row types ──
export type DbProgram = Tables<'programs'>;
export type DbWorkshop = Tables<'program_workshops'>;
export type DbMentorship = Tables<'program_mentorships'>;
export type DbChapter = Tables<'program_chapters'>;
export type DbChapterContent = Tables<'chapter_contents'>;
export type DbCertificate = Tables<'program_certificates'>;
export type DbWorkshopTool = Tables<'workshop_tools'>;
export type DbContentBank = Tables<'content_bank'>;

// ── Detail composite type ──
export interface ProgramDetail {
  program: DbProgram;
  workshops: (DbWorkshop & { tools: DbWorkshopTool[] })[];
  mentorships: DbMentorship[];
  chapters: (DbChapter & { contents: (DbChapterContent & { content_bank: DbContentBank | null })[] })[];
  certificates: DbCertificate[];
}

// ── Queries ──

export function useProgramDetail(programId: string | null) {
  return useQuery({
    queryKey: ['program-detail', programId],
    queryFn: async (): Promise<ProgramDetail | null> => {
      if (!programId) return null;

      // Fetch all in parallel
      const [progRes, wsRes, msRes, chRes, certRes] = await Promise.all([
        supabase.from('programs').select('*').eq('id', programId).single(),
        supabase.from('program_workshops').select('*').eq('program_id', programId).is('deleted_at', null).order('sort_order'),
        supabase.from('program_mentorships').select('*').eq('program_id', programId).is('deleted_at', null).order('sort_order'),
        supabase.from('program_chapters').select('*').eq('program_id', programId).order('sort_order'),
        supabase.from('program_certificates').select('*').eq('program_id', programId).order('private_name'),
      ]);

      if (progRes.error) throw progRes.error;

      // Fetch workshop tools
      const workshopIds = (wsRes.data || []).map(w => w.id);
      let workshopTools: DbWorkshopTool[] = [];
      if (workshopIds.length > 0) {
        const { data: toolsData } = await supabase
          .from('workshop_tools')
          .select('*')
          .in('workshop_id', workshopIds);
        workshopTools = toolsData || [];
      }

      // Fetch chapter contents with content_bank
      const chapterIds = (chRes.data || []).map(c => c.id);
      let chapterContents: any[] = [];
      if (chapterIds.length > 0) {
        const { data: ccData } = await supabase
          .from('chapter_contents')
          .select('*, content_bank(*)')
          .in('chapter_id', chapterIds)
          .order('sort_order');
        chapterContents = ccData || [];
      }

      return {
        program: progRes.data,
        workshops: (wsRes.data || []).map(ws => ({
          ...ws,
          tools: workshopTools.filter(t => t.workshop_id === ws.id),
        })),
        mentorships: msRes.data || [],
        chapters: (chRes.data || []).map(ch => ({
          ...ch,
          contents: chapterContents.filter(cc => cc.chapter_id === ch.id),
        })),
        certificates: certRes.data || [],
      };
    },
    enabled: !!programId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

// ── Mutations ──

export function useUpsertProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (program: {
      id?: string;
      name: string;
      description?: string | null;
      level?: number | null;
      category?: string | null;
      duration?: string | null;
      image_url?: string | null;
      modules_count?: number | null;
      skills?: string[] | null;
      color?: string | null;
      status?: 'active' | 'inactive' | 'suspended' | null;
      max_participants?: number | null;
      slug?: string | null;
      sequential_unlock?: boolean;
      modality?: 'cohort' | 'self_paced';
      is_featured?: boolean;
      has_mentorships?: boolean;
    }) => {
      if (program.id) {
        const { data, error } = await supabase
          .from('programs')
          .update(program)
          .eq('id', program.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { id: _id, ...rest } = program;
        const { data, error } = await supabase
          .from('programs')
          .insert(rest as any)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['programs'] });
      qc.invalidateQueries({ queryKey: ['program-detail', data.id] });
    },
  });
}

export function useDeleteProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string) => {
      // Delete sub-entities first (cascade may handle some, but be explicit)

      // Cohorts and their sub-entities
      const { data: cohorts } = await supabase.from('cohorts').select('id').eq('program_id', programId);
      if (cohorts && cohorts.length > 0) {
        const cohortIds = cohorts.map(c => c.id);
        await deleteCalendarEventsForScheduleItems({ cohortIds });
        await supabase.from('cohort_schedule_items').delete().in('cohort_id', cohortIds);
        await supabase.from('cohort_participants').delete().in('cohort_id', cohortIds);
        await supabase.from('cohort_mentors').delete().in('cohort_id', cohortIds);
        await supabase.from('cohort_enrollments').delete().in('cohort_id', cohortIds);
        await supabase.from('cohorts').delete().eq('program_id', programId);
      }

      // Workshops + tools
      const { data: workshops } = await supabase.from('program_workshops').select('id').eq('program_id', programId);
      if (workshops && workshops.length > 0) {
        const wsIds = workshops.map(w => w.id);
        await supabase.from('workshop_tools').delete().in('workshop_id', wsIds);
        await supabase.from('program_workshops').delete().eq('program_id', programId);
      }
      // Chapters + contents
      const { data: chapters } = await supabase.from('program_chapters').select('id').eq('program_id', programId);
      if (chapters && chapters.length > 0) {
        const chIds = chapters.map(c => c.id);
        await supabase.from('chapter_contents').delete().in('chapter_id', chIds);
        await supabase.from('program_chapters').delete().eq('program_id', programId);
      }
      // Mentorships
      await supabase.from('program_mentorships').delete().eq('program_id', programId);
      // Certificate
      await supabase.from('program_certificates').delete().eq('program_id', programId);
      // Certificate logs
      await supabase.from('certificate_logs').delete().eq('program_id', programId);
      // Program
      const { error } = await supabase.from('programs').delete().eq('id', programId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['programs'] });
    },
  });
}

export function useSaveProgramWorkshops() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      programId,
      workshops,
    }: {
      programId: string;
      workshops: { id?: string; title: string; description?: string; duration?: string; week_number?: number; sort_order?: number; material_link?: string; problema_concreto?: string; resultado_esperado?: string; tiempo_estimado?: string; tip_adicional?: string; tools: string[]; requires_deliverable?: boolean; deliverable_resource_url?: string | null }[];
    }) => {
      // 1. Fetch existing workshops for this program (only active)
      const { data: existing, error: existingErr } = await supabase
        .from('program_workshops')
        .select('id, title')
        .eq('program_id', programId)
        .is('deleted_at', null);
      if (existingErr) throw existingErr;
      const existingIds = new Set((existing || []).map(w => w.id));

      const incomingIds = new Set(
        workshops.map(w => w.id).filter((id): id is string => !!id && existingIds.has(id))
      );
      const finalWorkshops: { id: string; title: string; isNew: boolean }[] = [];

      // 2. Upsert: update existing (matched by id), insert new
      for (let i = 0; i < workshops.length; i++) {
        const ws = workshops[i];
        const row = {
          program_id: programId,
          title: ws.title,
          description: ws.description || null,
          duration: ws.duration || null,
          week_number: ws.week_number ?? i + 1,
          sort_order: ws.sort_order ?? i + 1,
          material_link: ws.material_link || null,
          problema_concreto: ws.problema_concreto || null,
          resultado_esperado: ws.resultado_esperado || null,
          tiempo_estimado: ws.tiempo_estimado || null,
          tip_adicional: ws.tip_adicional || null,
          requires_deliverable: ws.requires_deliverable ?? true,
          deliverable_resource_url: ws.deliverable_resource_url ?? null,
        };

        if (ws.id && existingIds.has(ws.id)) {
          // Update existing — preserves DB ID
          const { error: updErr } = await supabase.from('program_workshops').update(row).eq('id', ws.id);
          if (updErr) throw updErr;
          finalWorkshops.push({ id: ws.id, title: ws.title, isNew: false });
        } else {
          // Insert new (no id, or id is a local UUID not in DB)
          const { data: inserted, error } = await supabase
            .from('program_workshops')
            .insert(row)
            .select('id, title')
            .single();
          if (error) throw error;
          finalWorkshops.push({ id: inserted.id, title: inserted.title, isNew: true });
        }
      }

      // 3. Soft-delete workshops whose IDs are no longer present in the incoming list
      const toDelete = (existing || []).filter(w => !incomingIds.has(w.id));
      if (toDelete.length > 0) {
        const deleteIds = toDelete.map(w => w.id);
        // Remove tools (safe; no FK blocks)
        const { error: toolsErr } = await supabase.from('workshop_tools').delete().in('workshop_id', deleteIds);
        if (toolsErr) throw toolsErr;
        // Soft-delete the workshop rows
        const { error: delErr } = await supabase
          .from('program_workshops')
          .update({ deleted_at: new Date().toISOString() })
          .in('id', deleteIds);
        if (delErr) throw delErr;
        // Clean orphan schedule items (no FK; safe to physically delete)
        await deleteCalendarEventsForScheduleItems({ refIds: deleteIds, type: 'workshop' });
        const { error: schedErr } = await supabase
          .from('cohort_schedule_items')
          .delete()
          .in('ref_id', deleteIds)
          .eq('type', 'workshop');
        if (schedErr) throw schedErr;
      }

      // 4. Rebuild tools for all workshops
      const allWsIds = finalWorkshops.map(w => w.id);
      if (allWsIds.length > 0) {
        const { error: clrErr } = await supabase.from('workshop_tools').delete().in('workshop_id', allWsIds);
        if (clrErr) throw clrErr;
      }
      const toolRows: { workshop_id: string; tool_name: string }[] = [];
      finalWorkshops.forEach((fw, i) => {
        (workshops[i].tools || []).forEach(tool => {
          toolRows.push({ workshop_id: fw.id, tool_name: tool });
        });
      });
      if (toolRows.length > 0) {
        const { error: tErr } = await supabase.from('workshop_tools').insert(toolRows);
        if (tErr) throw tErr;
      }

      // 5. Sync ref_ids in cohort_schedule_items only for newly inserted workshops (legacy by title)
      const { data: cohorts } = await supabase
        .from('cohorts')
        .select('id')
        .eq('program_id', programId);
      if (cohorts && cohorts.length > 0) {
        const cohortIds = cohorts.map(c => c.id);
        for (const fw of finalWorkshops.filter(w => w.isNew)) {
          await supabase
            .from('cohort_schedule_items')
            .update({ ref_id: fw.id })
            .eq('title', fw.title)
            .eq('type', 'workshop')
            .in('cohort_id', cohortIds);
        }
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['program-detail', vars.programId] });
      qc.invalidateQueries({ queryKey: ['cohort-schedule'] });
    },
  });
}

export function useSaveProgramMentorships() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      programId,
      mentorships,
    }: {
      programId: string;
      mentorships: { id?: string; title: string; description?: string; duration?: string; week_number?: number; sort_order?: number }[];
    }) => {
      // 1. Fetch existing (active only)
      const { data: existing, error: existingErr } = await supabase
        .from('program_mentorships')
        .select('id, title')
        .eq('program_id', programId)
        .is('deleted_at', null);
      if (existingErr) throw existingErr;
      const existingIds = new Set((existing || []).map(m => m.id));
      const incomingIds = new Set(
        mentorships.map(m => m.id).filter((id): id is string => !!id && existingIds.has(id))
      );
      const finalMentorships: { id: string; title: string; isNew: boolean }[] = [];

      // 2. Upsert by id
      for (let i = 0; i < mentorships.length; i++) {
        const ms = mentorships[i];
        const row = {
          program_id: programId,
          title: ms.title,
          description: ms.description || null,
          duration: ms.duration || null,
          week_number: ms.week_number ?? i + 1,
          sort_order: ms.sort_order ?? i + 1,
        };
        if (ms.id && existingIds.has(ms.id)) {
          const { error: updErr } = await supabase.from('program_mentorships').update(row).eq('id', ms.id);
          if (updErr) throw updErr;
          finalMentorships.push({ id: ms.id, title: ms.title, isNew: false });
        } else {
          const { data: inserted, error } = await supabase
            .from('program_mentorships')
            .insert(row)
            .select('id, title')
            .single();
          if (error) throw error;
          finalMentorships.push({ id: inserted.id, title: inserted.title, isNew: true });
        }
      }

      // 3. Soft-delete by id
      const toDelete = (existing || []).filter(m => !incomingIds.has(m.id));
      if (toDelete.length > 0) {
        const deleteIds = toDelete.map(m => m.id);
        const { error: delErr } = await supabase
          .from('program_mentorships')
          .update({ deleted_at: new Date().toISOString() })
          .in('id', deleteIds);
        if (delErr) throw delErr;
        // Clean orphan schedule items (no FK)
        await deleteCalendarEventsForScheduleItems({ refIds: deleteIds, type: ['mentorship', 'mentoring'] });
        const { error: schedErr } = await supabase
          .from('cohort_schedule_items')
          .delete()
          .in('ref_id', deleteIds)
          .in('type', ['mentorship', 'mentoring']);
        if (schedErr) throw schedErr;
      }

      // 4. Sync ref_ids in cohort_schedule_items only for newly inserted (legacy by title)
      const { data: cohorts } = await supabase
        .from('cohorts')
        .select('id')
        .eq('program_id', programId);
      if (cohorts && cohorts.length > 0) {
        const cohortIds = cohorts.map(c => c.id);
        for (const fm of finalMentorships.filter(m => m.isNew)) {
          await supabase
            .from('cohort_schedule_items')
            .update({ ref_id: fm.id })
            .eq('title', fm.title)
            .in('type', ['mentorship', 'mentoring'])
            .in('cohort_id', cohortIds);
        }
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['program-detail', vars.programId] });
      qc.invalidateQueries({ queryKey: ['cohort-schedule'] });
    },
  });
}

export function useSaveProgramChapters() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      programId,
      chapters,
    }: {
      programId: string;
      chapters: {
        name: string;
        week_number?: number;
        sort_order?: number;
        unlock_conditions?: string[];
        contents: { content_id: string; is_mandatory: boolean; sort_order: number }[];
      }[];
    }) => {
      // Delete existing chapters + contents
      const { data: existing } = await supabase.from('program_chapters').select('id').eq('program_id', programId);
      if (existing && existing.length > 0) {
        await supabase.from('chapter_contents').delete().in('chapter_id', existing.map(c => c.id));
        await supabase.from('program_chapters').delete().eq('program_id', programId);
      }

      if (chapters.length === 0) return;

      const chRows = chapters.map((ch, i) => ({
        program_id: programId,
        name: ch.name,
        week_number: ch.week_number ?? i + 1,
        sort_order: ch.sort_order ?? i + 1,
        unlock_conditions: ch.unlock_conditions || null,
      }));

      const { data: inserted, error } = await supabase.from('program_chapters').insert(chRows).select();
      if (error) throw error;

      // Insert chapter contents
      const ccRows: { chapter_id: string; content_id: string; is_mandatory: boolean; sort_order: number }[] = [];
      (inserted || []).forEach((ch, i) => {
        (chapters[i].contents || []).forEach(cc => {
          ccRows.push({
            chapter_id: ch.id,
            content_id: cc.content_id,
            is_mandatory: cc.is_mandatory,
            sort_order: cc.sort_order,
          });
        });
      });
      if (ccRows.length > 0) {
        const { error: ccErr } = await supabase.from('chapter_contents').insert(ccRows);
        if (ccErr) throw ccErr;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['program-detail', vars.programId] });
    },
  });
}

export function useSaveProgramCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      programId,
      certificate,
    }: {
      programId: string;
      certificate: any;
    }) => {
      const { id, diploma_a, diploma_b, ...rest } = certificate;
      const payload: any = { ...rest, program_id: programId };

      // Flatten diploma A/B into diploma_a_* / diploma_b_* columns
      const flatten = (which: 'a' | 'b', d: any) => {
        if (!d) return;
        const fields = [
          'name', 'description', 'image_url', 'skills', 'entities',
          'additional_info', 'additional_url', 'priority',
          'require_content', 'min_content_percent',
          'require_deliverables', 'min_deliverables_percent',
          'require_project', 'require_project_approved', 'require_project_answered',
          'require_lab', 'layout',
        ];
        for (const f of fields) {
          if (d[f] !== undefined) payload[`diploma_${which}_${f}`] = d[f];
        }
      };
      flatten('a', diploma_a);
      flatten('b', diploma_b);

      // If is_default, unset others first
      if (payload.is_default) {
        await supabase
          .from('program_certificates')
          .update({ is_default: false })
          .eq('program_id', programId);
      }

      if (id) {
        const { data, error } = await supabase
          .from('program_certificates')
          .update(payload)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('program_certificates')
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['program-detail', vars.programId] });
      qc.invalidateQueries({ queryKey: ['program-certificates', vars.programId] });
    },
  });
}

export function useDeleteProgramCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ certificateId, programId }: { certificateId: string; programId: string }) => {
      const { error } = await supabase.from('program_certificates').delete().eq('id', certificateId);
      if (error) throw error;
      return { certificateId, programId };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['program-detail', data.programId] });
      qc.invalidateQueries({ queryKey: ['program-certificates', data.programId] });
    },
  });
}

export function useProgramCertificates(programId: string | null) {
  return useQuery({
    queryKey: ['program-certificates', programId],
    queryFn: async () => {
      if (!programId) return [];
      const { data, error } = await supabase
        .from('program_certificates')
        .select('*')
        .eq('program_id', programId)
        .order('private_name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!programId,
  });
}

export function useDuplicateProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string) => {
      // Load detail
      const { data: prog } = await supabase.from('programs').select('*').eq('id', programId).single();
      if (!prog) throw new Error('Program not found');

      // Insert copy
      const { id: _id, created_at: _ca, ...rest } = prog;
      // programs.status is entity_status (active|inactive|suspended); UI "Borrador" maps to inactive (see ProgramManagementPage statusMap)
      const { data: newProg, error } = await supabase
        .from('programs')
        .insert({ ...rest, name: `${prog.name} (copia)`, status: 'inactive', slug: null })
        .select()
        .single();
      if (error) throw error;
      const newId = newProg.id;

      // Copy workshops + tools (only active, not soft-deleted)
      const { data: workshops } = await supabase.from('program_workshops').select('*').eq('program_id', programId).is('deleted_at', null);
      if (workshops && workshops.length > 0) {
        const wsMap: Record<string, string> = {};
        for (const ws of workshops) {
          const { id: oldId, program_id: _pid, ...wsRest } = ws;
          const { data: newWs } = await supabase.from('program_workshops').insert({ ...wsRest, program_id: newId }).select().single();
          if (newWs) wsMap[oldId] = newWs.id;
        }
        // Copy tools
        const oldWsIds = Object.keys(wsMap);
        if (oldWsIds.length > 0) {
          const { data: tools } = await supabase.from('workshop_tools').select('*').in('workshop_id', oldWsIds);
          if (tools && tools.length > 0) {
            const toolRows = tools.map(t => ({ workshop_id: wsMap[t.workshop_id], tool_name: t.tool_name }));
            await supabase.from('workshop_tools').insert(toolRows);
          }
        }
      }

      // Copy mentorships (only active, not soft-deleted)
      const { data: mentorships } = await supabase.from('program_mentorships').select('*').eq('program_id', programId).is('deleted_at', null);
      if (mentorships && mentorships.length > 0) {
        const msRows = mentorships.map(ms => {
          const { id: _id, program_id: _pid, ...msRest } = ms;
          return { ...msRest, program_id: newId };
        });
        await supabase.from('program_mentorships').insert(msRows);
      }

      // Copy chapters + contents
      const { data: chapters } = await supabase.from('program_chapters').select('*').eq('program_id', programId);
      if (chapters && chapters.length > 0) {
        const chMap: Record<string, string> = {};
        for (const ch of chapters) {
          const { id: oldId, program_id: _pid, ...chRest } = ch;
          const { data: newCh } = await supabase.from('program_chapters').insert({ ...chRest, program_id: newId }).select().single();
          if (newCh) chMap[oldId] = newCh.id;
        }
        const oldChIds = Object.keys(chMap);
        if (oldChIds.length > 0) {
          const { data: ccData } = await supabase.from('chapter_contents').select('*').in('chapter_id', oldChIds);
          if (ccData && ccData.length > 0) {
            const ccRows = ccData.map(cc => ({
              chapter_id: chMap[cc.chapter_id],
              content_id: cc.content_id,
              is_mandatory: cc.is_mandatory,
              sort_order: cc.sort_order,
            }));
            await supabase.from('chapter_contents').insert(ccRows);
          }
        }
      }

      // Copy all certificates
      const { data: certs } = await supabase.from('program_certificates').select('*').eq('program_id', programId);
      if (certs && certs.length > 0) {
        const certRows = certs.map((cert: any) => {
          const { id: _cid, program_id: _cpid, created_at: _cca, updated_at: _cua, ...certRest } = cert;
          return { ...certRest, program_id: newId };
        });
        await supabase.from('program_certificates').insert(certRows);
      }

      return newProg;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['programs'] });
    },
  });
}

/**
 * Crea (o reutiliza) la cohorte perpetua de una ruta de autoaprendizaje.
 * Idempotente: el índice único parcial garantiza una sola perpetua por ruta.
 */
export function useEnsurePerpetualCohort() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ programId, programName }: { programId: string; programName: string }) => {
      const { data: existing } = await supabase
        .from('cohorts')
        .select('id')
        .eq('program_id', programId)
        .eq('is_perpetual', true)
        .maybeSingle();
      if (existing) return existing.id;

      const { data, error } = await supabase
        .from('cohorts')
        .insert({
          program_id: programId,
          name: `${programName} — Autoaprendizaje`,
          is_perpetual: true,
          is_public: false,
          enrollment_open: true,
          status: 'active' as any,
        })
        .select('id')
        .maybeSingle();

      if (error || !data) {
        // Posible carrera: releer
        const { data: retry } = await supabase
          .from('cohorts')
          .select('id')
          .eq('program_id', programId)
          .eq('is_perpetual', true)
          .maybeSingle();
        if (retry) return retry.id;
        throw error ?? new Error('No se pudo crear la cohorte de autoaprendizaje');
      }
      return data.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cohorts'] });
    },
  });
}
